// 客（来店する宇宙人）のカタログ。純データ＝描画にも DOM にも乱数にも依存しない。
//
// M3 の核は「庭の属性 → どの客が来るか」。各客は出現条件（gardenAttributes に
// 対する述語）と、来店ごとのチップ・滞在時間・フレーバー・見た目パラメータを持つ。
// 出現条件と頻度（基礎レート + 香りブースト等）を分けて持ち、simulate がレートを
// 使って決定的に抽選する。

import type { AttributeKey } from './decos';
import type { DielType, TimeOfDay } from './daynight';
import { dielMultiplier } from './daynight';
import type { Season } from './season';
import { seasonalArrivalMultiplier } from './season';

/**
 * 庭属性の合計（gardenAttributes の戻り値と同じ形）。
 *
 * M4 で「古木の本数」も来店条件に使えるよう、属性以外の派生量を任意フィールドで
 * 持てるようにした。属性 7 種は必須、`ancientTrees`（古木の本数）は任意（未指定は
 * 0 とみなす）。これにより既存の属性だけの判定はそのまま動く。
 */
export type GardenAttributeTotals = Record<AttributeKey, number> & {
  /** 庭にある古木（最終成長段階の植物）の本数。M4 のレア客条件に使う。 */
  ancientTrees?: number;
};

/** 客の見た目パラメータ（render 層が「もこもこブロブ」を作るのに使う）。 */
export interface CustomerLook {
  /** 体の基調色（パステル、#RRGGBB）。 */
  bodyColor: string;
  /** アクセント色（耳・羽根・装飾）。 */
  accentColor: string;
  /**
   * 形のバリエーション。render 層が差分パーツを足してシルエット差を作る:
   *  blob=房／moth=羽根＋触角／crystal=背の結晶びれ／drop=しずく頭／
   *  crescent=三日月の角／sprig=頭に小枝。
   */
  shape: 'blob' | 'moth' | 'crystal' | 'drop' | 'crescent' | 'sprig';
  /** ベースの大きさ倍率（1.0 = 標準）。 */
  scale: number;
}

/** 客 1 種の定義。 */
export interface CustomerDef {
  /** 安定 ID（セーブ・図鑑に焼かれるので将来も変えない）。 */
  id: string;
  /** 表示名（日本語）。 */
  name: string;
  /** UI のアイコン用絵文字。 */
  icon: string;
  /**
   * 出現条件。庭属性の合計を受けて「来店し得るか」を返す述語。
   * false なら抽選対象にすらならない（例: 光が足りない蛾型）。
   */
  appears(attrs: GardenAttributeTotals): boolean;
  /**
   * 基礎来店レート（1 分あたりの期待来店回数の素）。simulate はこれに
   * 香りブースト等の係数を掛けて確率に使う。レアほど小さい。
   */
  baseRate: number;
  /**
   * この客が好む「キー属性」（v0.8.0 バランス改修）。庭にこの属性が多いほど来店頻度が
   * 上がる（飽和付き）。属性ごとに別々の客が紐づくので、どの属性に注力しても「自分の客が
   * 多く来る」形になり、戦略（focus）が同格かつ個性的になる。scent だけが頻度を増やす旧仕様
   * （scentBoost）を、全属性対称な keyAttribute + rateGain に置き換えた。
   */
  keyAttribute: AttributeKey;
  /**
   * キー属性による来店頻度の最大ブースト幅（飽和時に rate×(1+rateGain) まで増える）。
   * 飽和曲線（attr/(attr+RATE_BOOST_HALF)）で逓減しながら漸近するので、1 属性に積み続けても
   * 青天井にならない（snowball 防止）。レアな客ほど大きめにして「揃えた庭への報酬」にする。
   */
  rateGain: number;
  /** 来店 1 回で落とすチップ。 */
  tip: number;
  /** 滞在時間（秒）。render の歩き回り→帰宅のタイミングに使う。 */
  dwellSec: number;
  /** 初訪問時に図鑑に出るひとことフレーバー。 */
  flavor: string;
  /** 見た目パラメータ。 */
  look: CustomerLook;
  /**
   * 昼夜タイプ（v0.6.0）。来店レートが時間帯で振れる（昼型は昼に来やすい等）。
   * 'any' は終日フラット。
   */
  diel: DielType;
  /**
   * 好む季節（v0.6.0、null = 季節無関係）。好む季節で来やすく、対の季節で少し控えめ。
   */
  favoriteSeason: Season | null;
}

/**
 * 客カタログ（6 種）。順序は図鑑の表示順（基本→レアの並び）。
 *
 * 出現条件は GAME_DESIGN.md の例に沿う:
 *  - もこもこ系の基本客: 条件なし（香りで頻度ブースト）
 *  - 蛾型ルナモス: 光≥3 かつ にぎやか≤3
 *  - 水辺好きパドル: 水辺≥2
 *  - 静けさを好むユメミ（自由デザイン）: 静寂≥4 かつ にぎやか≤1
 *  - 結晶獣プリズム（レア）: 結晶≥5 かつ 水辺≥1
 *  - 樹守ねむ（M4 レア）: 古木≥1 かつ 緑≥4（盆栽の古木が育った庭の象徴客）
 */
export const CUSTOMER_CATALOG: readonly CustomerDef[] = [
  {
    id: 'mokomoke',
    name: 'モコモケ',
    icon: '🟣',
    appears: () => true, // 基本客。いつでも来る（香りで頻度が上がる）
    // v0.5.1: 「客がいない時間」を無くすため基礎レートを底上げ（1.0→1.6）。
    // TICK_SEC=30 と合わせ、評判 0 でも 30 秒刻みで ~0.8 の確率＝数十秒に 1 体は来る。
    baseRate: 1.6,
    keyAttribute: 'scent',
    rateGain: 1.6,
    tip: 2,
    dwellSec: 18,
    flavor: 'ふわふわの毛玉星人。庭の香りにつられてやってくる常連さん。',
    look: { bodyColor: '#F4B9D6', accentColor: '#FFE3A8', shape: 'blob', scale: 1.0 },
    diel: 'any', // 常連は終日来る
    favoriteSeason: 'spring', // 春が少し好き（花の香り）
  },
  {
    id: 'lunamoth',
    name: 'ルナモス',
    icon: '🦋',
    // 光に集まる蛾型。にぎやかすぎると怖がって来ない。
    appears: (a) => a.light >= 3 && a.lively <= 3,
    baseRate: 1.0, // v0.5.1: 賑わい底上げ（0.7→1.0）
    keyAttribute: 'light',
    rateGain: 1.8,
    tip: 3,
    dwellSec: 14,
    flavor: '光を慕う夜の蛾型。ランタンの灯りに静かに羽を休めにくる。',
    look: { bodyColor: '#C8B6F0', accentColor: '#FFD79A', shape: 'moth', scale: 1.05 },
    diel: 'nocturnal', // 夜の蛾型。夜・夕に来やすい
    favoriteSeason: 'summer', // 夏の夜に羽を休める
  },
  {
    id: 'paddle',
    name: 'パドル',
    icon: '💧',
    // 水辺が好き。池や噴水があると来る。
    appears: (a) => a.water >= 2,
    baseRate: 1.0, // v0.5.1: 賑わい底上げ（0.6→0.9）
    keyAttribute: 'water',
    rateGain: 1.8,
    tip: 4,
    dwellSec: 20,
    flavor: '水辺を探して旅する雫型。池のふちでぷかぷか涼んでいく。',
    look: { bodyColor: '#8FD8EE', accentColor: '#BDEFE0', shape: 'drop', scale: 0.95 },
    diel: 'diurnal', // 昼に水辺で涼む
    favoriteSeason: 'summer', // 夏が大好き
  },
  {
    id: 'yumemi',
    name: 'ユメミ',
    icon: '🌙',
    // 静けさを好む（自由デザイン）。にぎやかが混ざると来ない。
    appears: (a) => a.calm >= 4 && a.lively <= 1,
    baseRate: 0.8, // v0.5.1: 賑わい底上げ（0.5→0.8）
    keyAttribute: 'calm',
    rateGain: 1.8,
    tip: 4,
    dwellSec: 24,
    flavor: '静寂のなかにだけ現れる夢見星人。ベンチでうとうと長居していく。',
    look: { bodyColor: '#C3B4EC', accentColor: '#FFF3D6', shape: 'crescent', scale: 1.1 },
    diel: 'nocturnal', // 夜の夢見星人
    favoriteSeason: 'winter', // 冬の静けさを好む
  },
  {
    id: 'prism',
    name: 'プリズム獣',
    icon: '💠',
    // レア。結晶がたくさん＋水辺の反射がある庭にだけ来る。
    appears: (a) => a.crystal >= 5 && a.water >= 1,
    baseRate: 0.4,
    keyAttribute: 'crystal',
    rateGain: 2.0,
    tip: 8,
    dwellSec: 16,
    flavor: '結晶の森と水鏡が揃った庭にだけ降りるレアな獣。光を屈折させて去る。',
    look: { bodyColor: '#A8E0F2', accentColor: '#F6B8E0', shape: 'crystal', scale: 1.15 },
    diel: 'nocturnal', // 夜に降りる
    favoriteSeason: 'winter', // 冬の澄んだ空気に現れやすい（季節限定の後押し）
  },
  {
    id: 'nemu',
    name: '樹守ねむ',
    icon: '🌳',
    // M4 レア。盆栽が古木まで育ち、緑ゆたかな庭にだけ現れる「庭の象徴」客。
    appears: (a) => (a.ancientTrees ?? 0) >= 1 && a.green >= 4,
    baseRate: 0.5,
    keyAttribute: 'green',
    rateGain: 2.0,
    tip: 10,
    dwellSec: 30,
    flavor: '古木のうろに棲まう、まどろみ好きの樹守。年経た木のある庭にだけ根を下ろしにくる。',
    look: { bodyColor: '#A8D98C', accentColor: '#8A6A4A', shape: 'sprig', scale: 1.25 },
    diel: 'diurnal', // 木漏れ日のなかでまどろむ
    favoriteSeason: 'autumn', // 秋の古木に根を下ろす
  },
];

/** ID → 客定義の索引。未知 ID は undefined。 */
const CUSTOMER_BY_ID: ReadonlyMap<string, CustomerDef> = new Map(
  CUSTOMER_CATALOG.map((c) => [c.id, c]),
);

/** ID から客定義を引く。未知 ID なら undefined。 */
export function getCustomer(id: string): CustomerDef | undefined {
  return CUSTOMER_BY_ID.get(id);
}

/**
 * 来店レート算出に渡す環境（v0.6.0）。時間帯と季節で来店係数が振れる。
 * 省略・null のときは係数 1.0（環境の影響なし）として従来挙動になる。
 */
export interface VisitEnv {
  /** 現在の時間帯（昼夜タイプの係数に使う）。null = 影響なし。 */
  tod?: TimeOfDay | null;
  /** ホーム島の現在の季節（好む季節の係数に使う）。null = 影響なし。 */
  season?: Season | null;
  /**
   * アンロック済みの季節別館（拡張島）の季節（v0.8.1）。その季節を好む客は「定住の常連」
   * として強めに底上げされる（seasonalArrivalMultiplier）。省略時は別館なし扱い。
   */
  islandSeasons?: Season[];
}

/**
 * キー属性ブーストの飽和半値点（v0.8.0）。庭のキー属性がこの値のとき、来店ブーストは
 * 最大幅（rateGain）のちょうど半分まで伸びる。大きいほど「もっと積め」が長く効き、
 * 小さいほど早く頭打ちになる。全属性で共通＝どの focus も同じ曲線で同格。
 */
export const RATE_BOOST_HALF = 24;

/**
 * キー属性の総量 → 来店頻度ブースト倍率（1..1+rateGain）。飽和曲線で逓減しながら漸近する
 * ので、1 属性に積み続けても青天井にならない（旧 scentBoost の snowball を解消）。
 */
export function rateBoost(keyAttrTotal: number, rateGain: number): number {
  const a = Math.max(0, keyAttrTotal);
  return 1 + rateGain * (a / (a + RATE_BOOST_HALF));
}

/**
 * 庭属性のもとで、各客の「1 分あたり来店レート」を算出する純関数。
 * 出現条件を満たさない客はレート 0。満たす客は baseRate に「キー属性ブースト」（飽和付き）、
 * さらに時間帯（昼夜タイプ）・季節（好む季節）の係数を掛ける（v0.6.0 / v0.8.0）。
 *
 * v0.8.0: 来店頻度を増やすのが scent だけだった旧仕様を改め、各客が好む keyAttribute で
 * 頻度が伸びる対称設計にした（mokomoke=香り / lunamoth=光 / paddle=水辺 / yumemi=静寂 /
 * prism=結晶 / nemu=緑）。どの属性に注力しても「自分の客が多く来る」ので戦略が同格・個性的。
 *
 * simulate はこのレートを使って分刻みで決定的に抽選する。レート計算と抽選を分けることで、
 * テストでレートだけを直接検証できる。env を省略すると環境係数は 1.0 になる。
 */
export function customerRates(
  attrs: GardenAttributeTotals,
  env?: VisitEnv,
): { id: string; ratePerMin: number }[] {
  const tod = env?.tod ?? null;
  const season = env?.season ?? null;
  const islandSeasons = env?.islandSeasons ?? [];
  return CUSTOMER_CATALOG.map((c) => {
    if (!c.appears(attrs)) return { id: c.id, ratePerMin: 0 };
    const boost = rateBoost(attrs[c.keyAttribute], c.rateGain);
    const dielMult = tod ? dielMultiplier(c.diel, tod) : 1.0;
    // 季節係数: ホームの移ろう季節と、アンロック済み季節別館の好ましい方を採用（v0.8.1）。
    const seasonMult = season
      ? seasonalArrivalMultiplier(c.favoriteSeason, season, islandSeasons)
      : 1.0;
    return { id: c.id, ratePerMin: c.baseRate * boost * dielMult * seasonMult };
  });
}
